import { api } from './api.ts'
import { corpoPrenotazione, mimeDelFile } from './regole-foto.ts'

/** Impronta SHA-256 in esadecimale, nella forma che chiede la conferma. */
async function impronta(dati: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await dati.arrayBuffer())
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

/**
 * Caricamento di un documento della scheda in tre tempi (apps/api/src/rotte/documenti.ts):
 * prenotazione, che restituisce l'URL prefirmato; invio del file direttamente all'archivio con la
 * cifratura lato server; conferma con l'impronta, che mette il documento in coda per l'antivirus.
 *
 * «Scegli un file» e «Fotografa il documento» passano entrambi da qui: stesso percorso cifrato,
 * stesse verifiche, stessa coda antivirus (D-03). Restituisce l'identificativo del documento.
 */
export async function caricaDocumento(
  personaId: string,
  documento: {
    nome: string; tipo: string; dati: Blob; mime?: string
    /** Campi che il modulo di caricamento aggiunge e la foto del documento non usa (DOC-16). */
    data_documento?: string | null
    scadenza?: string | null
    nota?: string | null
    sostituisce?: string | null
    /** Progetto del modulo del destinatario firmato (492). */
    progetto_id?: string | null
  },
): Promise<string> {
  const mime = documento.mime ?? mimeDelFile(documento.nome, documento.dati.type)
  const prenotazione = await api.post<{ id: string; url_caricamento: string }>(
    `/persone/${personaId}/documenti`,
    {
      ...corpoPrenotazione({ nome: documento.nome, mime, dimensione: documento.dati.size, tipo: documento.tipo }),
      // Campi facoltativi: si inviano solo se valorizzati, cosi' la foto del documento continua
      // a mandare esattamente il corpo di prima.
      ...(documento.data_documento ? { data_documento: documento.data_documento } : {}),
      ...(documento.scadenza ? { scadenza: documento.scadenza } : {}),
      ...(documento.nota ? { nota: documento.nota } : {}),
      ...(documento.sostituisce ? { sostituisce: documento.sostituisce } : {}),
      ...(documento.progetto_id ? { progetto_id: documento.progetto_id } : {}),
    },
  )
  const risposta = await fetch(prenotazione.url_caricamento, {
    method: 'PUT',
    headers: { 'content-type': mime, 'x-amz-server-side-encryption': 'AES256' },
    body: documento.dati,
  })
  if (!risposta.ok) throw new Error(`archiviazione rifiutata (codice ${risposta.status})`)
  await api.post(`/persone/${personaId}/documenti/${prenotazione.id}/conferma`,
    { sha256: await impronta(documento.dati) })
  return prenotazione.id
}
